import React, {lazy, useEffect, useState, Suspense, memo} from 'react'
import cx from 'classnames'
import {connect} from 'react-redux'

/* Components */
import OtherMerchantBox from './OtherMerchantBox'

/* Constants */
import {MODAL_IDS} from '../../../../modules/modal/modalConstants'
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Actions */
import {actionCreators as modalActionCreators} from '../../../../redux/actions/modalActions'

/* Utils */
import {getModalStateFlagName} from '../../../../modules/modal/modalUtils'
import DataSet from '../../../../utils/DataSet'
import {yieldToMain} from '../../../../utils/performance'

/* Styles */
import s from './other-merchants.scss'

/* Lazy Components */
const OtherMerchantsModal = lazy(() => import('./modal/OtherMerchantModal'))

const OtherMerchants = memo(({
  dispatch,
  className,
  listings,
  history,
  match,
  name,
  brandName,
  sku,
  imageUrl,
  flags,
  pageTypeForDavinci,
  categoryId,
  categoryName,
  productId,
  merchantName,
  merchantId,
  listingId,
  categories,
  productStatus,
  reviewRate,
  reviewCount,
  price,
  isAddToCartPending,
  formattedMinimumPriceOfListing,
  formattedMaximumPriceOfListing,
  allListings,
  product,
  userInfo,
  dueDate,
  hasMoreListings,
  otherSellersCampaigns,
  otherSellerCoupon,
  allOtherSellerCampaign
}) => {
  const [isFastSellerClick, setIsFastSellerClick] = useState(false)
  const [delivery, setDelivery] = useState(dueDate?.length > 0 ? dueDate : null)

  useEffect(() => {
    window?.HbEventBus?.on('storefront.voltran.deliveryOptions.fasterSeller.click', (isFastSeller) => {
      handleOtherMerchantsModal(isFastSeller)
    })
  }, [])

  useEffect(() => {
    if (dueDate?.length > 0) {
      setDelivery(dueDate)
    }
  }, [dueDate])

  const handleOtherMerchantsModal = async(isFastSeller = false) => {
    // eslint-disable-next-line no-unused-expressions
    setIsFastSellerClick(isFastSeller)
    await yieldToMain()
    dispatch(modalActionCreators.openModal(MODAL_IDS.OTHER_MERCHANTS_MODAL))
  }

  const handleAllOtherMerchantsClick = async() => {
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.OTHER_MERCHANTS_CLICK, {
      placement: 'Other Merchants',
      page_type: 'product detail',
      category_hierarchy: categories.map((item) => ({
        id: item?.categoryId,
        name: item?.categoryName
      })),
      merchant: {
        id: merchantId,
        name: merchantName
      },
      category: {
        id: categoryId,
        name: categoryName
      },
      products: [
        {
          product_id: productId,
          price: `${price}`,
          sku: sku,
          product_status: productStatus,
          shipping_type: '',
          listing_id: listingId,
          position: 1
        }
      ]
    })
    await yieldToMain()
    handleOtherMerchantsModal(false)
  }

  return (
    <>
      <div
        className={cx(s.root, className)} data-test-id={'other-merchants'}
        data-hbus={
          DataSet.createComponentViewDataForDavinci({
            pageType: pageTypeForDavinci,
            pageValue: sku,
            placement: 'Other Sellers',
            component: 'Other Sellers',
            categoryHierarchy: `${categoryId} - ${categoryName}`,
            productId: productId,
            merchantName: merchantName,
            merchantId: merchantId,
            listingId: listingId,
            position: '1',
            price: `${price}`,
            priceRange: `${formattedMinimumPriceOfListing} - ${formattedMaximumPriceOfListing}`,
            otherSeller: listings?.slice(0, 2)?.map((item) => ({
              merchant_name: item?.merchantName,
              merchant_id: item?.merchantId,
              merchant_label: item?.merchantInfo?.merchantLabels?.[0]?.labelName,
              merchant_coupon: false,
              merchant_score: parseFloat(item?.merchantInfo?.ratingSummary?.lifetimeRating).toFixed(1).
                replace('.', ',')
            }))
          })}>
        <div className={s.titleContainer}>
          <span className={s.title}>
            {'Diğer satıcılar'}
          </span>
          {
            hasMoreListings &&
            <button className={s.seeAllButton} onClick={() => handleAllOtherMerchantsClick()}>
              {'Tümünü gör'}
              <div className={s.angleRightIcon}></div>
            </button>
          }
        </div>
        <div className={s.merchants} data-test-id={'other-merchants'}>
          {
            listings?.slice(0, 2)?.map?.((listing, index) => (
              <OtherMerchantBox
                dispatch={dispatch}
                type={'row'}
                listing={listing}
                name={name}
                listings={listings}
                position={index + 1}
                pageTypeForDavinci={pageTypeForDavinci}
                sku={sku}
                merchantName={merchantName}
                merchantId={merchantId}
                imageUrl={imageUrl}
                isAddToCartPending={isAddToCartPending}
                delivery={delivery}
                history={history}
                reviewRate={reviewRate}
                campaign={otherSellersCampaigns?.find((item) => item?.merchantId === listing?.merchantId)}
                coupon={otherSellerCoupon?.find((item) => item?.merchantId === listing?.merchantId)}
                reviewCount={reviewCount}
                price={price}
                match={match}/>
            ))
          }
        </div>
      </div>
      {
        process.env.BROWSER && Boolean(flags[getModalStateFlagName(MODAL_IDS.OTHER_MERCHANTS_MODAL)]) &&
        <Suspense fallback={<div/>}>
          <OtherMerchantsModal
            dispatch={dispatch}
            listings={listings}
            name={name}
            history={history}
            match={match}
            sku={sku}
            merchantName={merchantName}
            merchantId={merchantId}
            isFastSellerClick={isFastSellerClick}
            brandName={brandName}
            delivery={delivery}
            pageTypeForDavinci={pageTypeForDavinci}
            imageUrl={imageUrl}
            reviewRate={reviewRate}
            product={product}
            userInfo={userInfo}
            isAddToCartPending={isAddToCartPending}
            reviewCount={reviewCount}
            productId={productId}
            listingId={listingId}
            categoryId={categoryId}
            allListings={allListings}
            categoryName={categoryName}
            otherSellerCoupon={otherSellerCoupon}
            allOtherSellerCampaign={allOtherSellerCampaign}
            price={price}
            formattedMinimumPriceOfListing={formattedMinimumPriceOfListing}
            formattedMaximumPriceOfListing={formattedMaximumPriceOfListing}
            otherMerchantsModal={Boolean(flags[getModalStateFlagName(MODAL_IDS.OTHER_MERCHANTS_MODAL)])}/>
        </Suspense>
      }
    </>
  )
})

function mapStateToProps(state) {
  const {
    modalState: {
      flags,
    },
    pendingState: {
      isAddToCartPending
    },
    productState: {
      otherSellersCampaigns,
      otherSellerCoupon,
      allOtherSellerCampaign
    }
  } = state;

  return {
    flags,
    isAddToCartPending,
    otherSellersCampaigns,
    otherSellerCoupon,
    allOtherSellerCampaign
  }
}

export default connect(mapStateToProps)(
  OtherMerchants
)

